import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
    UserRound,
    Mail,
    Phone,
    MapPin,
    IdCard,
    Building2,
    Briefcase,
    UserCog,
    Clock,
    Landmark,
    FileText,
    Package,
    Wallet,
    Camera,
    Download,
    Loader2,
    Droplet,
    Cake,
    Users,
    ShieldAlert,
    ReceiptText,
    ChevronLeft,
    ChevronRight,
    Eye,
    Activity,
    Bell,
    Scale,
    PiggyBank,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import { breakupEntriesForDisplay } from "../../utils/payrollBreakup";
import { DatePicker, Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { openPayslipPdf } from "../../components/common/CommonFunctions";
import { EMPLOYEE_PROFILE, PAYROLL_PAY_SLIP } from "../../constants/api";
import male from "../../assets/svg/Male.svg";
import EmployeeActivityContent from "./helpers/EmployeeActivityContent";
import MyProfileNotificationsTab from "./helpers/MyProfileNotificationsTab";
import MyProfileDepositsTab from "./helpers/MyProfileDepositsTab";
import MyProfilePenaltiesTab from "./helpers/MyProfilePenaltiesTab";
import PayrollProfileLockOverlay from "../payroll/PayrollProfileLockOverlay";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";

/* ---- helpers ---- */

// Render a value, falling back to an em-dash for null/blank.
const show = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const fmtDate = (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

// ₹ formatting (Indian grouping); blank/empty -> em-dash.
const fmtINR = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return "—";
    return `₹ ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
};

// One label/value line.
const Field = ({ icon: Icon, label, value, className }) => (
    <div className={cn("flex items-start gap-2.5", className)}>
        {Icon && <Icon className="w-4 h-4 mt-0.5 shrink-0 text-sidebar-primary/70" />}
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide font-medium text-gray-400 dark:text-gray-500">{label}</p>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">{show(value)}</p>
        </div>
    </div>
);

// Section wrapper card.
const SectionCard = ({ icon: Icon, title, children, action }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4 text-sidebar-primary" />} {title}
            </h3>
            {action}
        </div>
        {children}
    </div>
);

const EmptyState = ({ text }) => (
    <p className="text-sm text-gray-400 italic py-6 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
        {text}
    </p>
);

// A small figure tile (CTC / Gross / Net).
const Figure = ({ label, value, tone = "gray" }) => {
    const tones = {
        gray: "bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200",
        teal: "bg-sidebar-primary/10 text-sidebar-primary",
        green: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300",
    };
    return (
        <div className={cn("rounded-xl px-3 py-2.5", tones[tone])}>
            <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
            <p className="text-base font-bold mt-0.5 tabular-nums">{value}</p>
        </div>
    );
};

// One salary record: key figures + earnings/deductions breakup.
const SalaryCard = ({ salary: s, isCurrent }) => {
    const earnings = Array.isArray(s.earnings) ? s.earnings : [];
    const deductions = Array.isArray(s.deductions) ? s.deductions : [];
    const hasFigures = s.annual_ctc !== "" || s.monthly_ctc !== "" || s.net_salary !== "";

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-sidebar-primary" />
                    Salary
                    <span className="text-[11px] font-normal normal-case text-gray-400">· {fmtDate(s.created_date)}</span>
                </h3>
                {isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                        Current
                    </span>
                )}
            </div>

            {/* Figures */}
            {hasFigures ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
                    <Figure label="Annual CTC" value={fmtINR(s.annual_ctc)} tone="teal" />
                    <Figure label="Monthly CTC" value={fmtINR(s.monthly_ctc)} />
                    <Figure label="Gross / mo" value={fmtINR(s.gross_salary_monthly_amount)} />
                    <Figure label="Net Pay" value={fmtINR(s.net_salary)} tone="green" />
                </div>
            ) : (
                <p className="text-xs text-gray-400 italic mb-1">No amounts recorded for this entry.</p>
            )}

            {/* Earnings + Deductions */}
            {(earnings.length > 0 || deductions.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {earnings.length > 0 && (
                        <div>
                            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-2">Earnings</p>
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                    {earnings.map((e) => (
                                        <tr key={e.id} className="text-gray-700 dark:text-gray-200">
                                            <td className="py-1.5">{e.name}</td>
                                            <td className="py-1.5 text-right tabular-nums font-medium">{fmtINR(e.monthly_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {deductions.length > 0 && (
                        <div>
                            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-2">Deductions</p>
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                    {deductions.map((d) => (
                                        <tr key={d.id} className="text-gray-700 dark:text-gray-200">
                                            <td className="py-1.5">{d.name}</td>
                                            <td className="py-1.5 text-right tabular-nums font-medium">{fmtINR(d.employee_share_monthly_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Renders a breakup dict as a simple two-column table (supports ea:* allowance credit objects).
const BreakupList = ({ title, data, valueClass }) => {
    const entries = breakupEntriesForDisplay(data);
    if (!entries.length) return null;
    return (
        <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-2">{title}</p>
            <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {entries.map(({ key, label, amount }) => (
                        <tr key={key} className="text-gray-700 dark:text-gray-200">
                            <td className="py-1.5">{label}</td>
                            <td className={cn("py-1.5 text-right tabular-nums font-medium", valueClass)}>{fmtINR(amount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/* =====================================================
   PAYSLIPS TAB (self-service, month-wise)
===================================================== */
const PayslipsTab = ({ locked = false }) => {
    const { fetch } = useApiMethods();
    const [month, setMonth] = useState(dayjs().month() + 1); // 1-12
    const [year, setYear] = useState(dayjs().year());
    const [slip, setSlip] = useState(null);
    const [loading, setLoading] = useState(false);

    const selected = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
    const atCurrent = selected.isSame(dayjs(), "month") || selected.isAfter(dayjs(), "month");

    const monthOptions = useMemo(
        () => Array.from({ length: 12 }, (_, i) => ({
            value: i + 1,
            label: dayjs().month(i).format("MMMM"),
        })),
        [],
    );
    const yearOptions = useMemo(() => {
        const y = dayjs().year();
        return Array.from({ length: 8 }, (_, i) => ({ value: y - i, label: String(y - i) }));
    }, []);

    const hasBreakupContent = slip && (
        breakupEntriesForDisplay(slip.earnings_breakup).length > 0
        || breakupEntriesForDisplay(slip.deductions_breakup).length > 0
        || breakupEntriesForDisplay(slip.credits_breakup).length > 0
        || breakupEntriesForDisplay(slip.debit_breakup).length > 0
        || breakupEntriesForDisplay(slip.reimbursements).length > 0
    );

    useEffect(() => {
        if (locked) {
            setSlip(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetch(PAYROLL_PAY_SLIP, {
                    is_personal: true,
                    payroll_month: month,
                    payroll_year: year,
                    page_size: 1,
                });
                if (cancelled) return;
                if (res?.status === "success") {
                    setSlip((res.data?.result || [])[0] || null);
                } else {
                    setSlip(null);
                    ToastMsg("error", res?.message || "Failed to load payslip.");
                }
            } catch (err) {
                if (!cancelled) {
                    setSlip(null);
                    ToastMsg("error", err?.message || "Failed to load payslip.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, year, locked]);

    const step = (delta) => {
        const d = selected.add(delta, "month");
        setMonth(d.month() + 1);
        setYear(d.year());
    };

    const payslipId = slip?.id || null;

    return (
        <div className="space-y-3">
            {/* Toolbar: month stepper + PDF actions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => step(-1)}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                        aria-label="Previous month"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => step(1)}
                        disabled={atCurrent}
                        className={cn(
                            "h-9 w-9 inline-flex items-center justify-center rounded-lg border transition-colors",
                            atCurrent
                                ? "border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                        )}
                        aria-label="Next month"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <Select
                    size="middle"
                    value={month}
                    onChange={(m) => setMonth(m)}
                    options={monthOptions}
                    className="min-w-[130px]"
                    aria-label="Payslip month"
                />
                <Select
                    size="middle"
                    value={year}
                    onChange={(y) => setYear(y)}
                    options={yearOptions}
                    className="w-[100px]"
                    aria-label="Payslip year"
                />
                <DatePicker
                    picker="month"
                    value={selected}
                    onChange={(d) => {
                        if (!d) return;
                        setMonth(d.month() + 1);
                        setYear(d.year());
                    }}
                    allowClear={false}
                    disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
                    className="max-w-[160px]"
                />
                {loading && <Loader2 className="w-4 h-4 animate-spin text-sidebar-primary" />}

                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        disabled={!payslipId}
                        onClick={() => payslipId && openPayslipPdf(payslipId)}
                        className={cn(
                            "h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border text-sm font-medium transition-colors",
                            payslipId
                                ? "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                                : "border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                        )}
                    >
                        <Eye className="w-4 h-4" /> View
                    </button>
                    <button
                        type="button"
                        onClick={() => payslipId && openPayslipPdf(payslipId, {
                            download: true,
                            fileName: `Payslip_${selected.format("MMMM_YYYY")}.pdf`,
                        })}
                        disabled={!payslipId}
                        className={cn(
                            "h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors text-white",
                            payslipId ? "bg-sidebar-primary hover:bg-sidebar-primary/90" : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                        )}
                    >
                        <Download className="w-4 h-4" /> Download
                    </button>
                </div>
            </div>

            {/* Body */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-sidebar-primary" />
                </div>
            ) : !slip ? (
                <SectionCard icon={ReceiptText} title="Payslip">
                    <EmptyState text={`No published payslip for ${selected.format("MMMM YYYY")}.`} />
                </SectionCard>
            ) : (
                <>
                    {/* Figures */}
                    <SectionCard icon={ReceiptText} title={`Payslip · ${selected.format("MMMM YYYY")}`}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            <Figure label="Net Pay" value={fmtINR(slip.net_pay)} tone="green" />
                            <Figure label="Earnings" value={fmtINR(slip.earnings)} tone="teal" />
                            <Figure label="Deductions" value={fmtINR(slip.deductions)} />
                            <Figure label="LOP" value={fmtINR(slip.lop_amount)} />
                        </div>
                        {(slip.lop_days || slip.paid_salary !== "" || slip.reimbursements_total) ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                                <Field label="Paid Salary" value={fmtINR(slip.paid_salary)} />
                                <Field label="LOP Days" value={show(slip.lop_days)} />
                                <Field label="Reimbursements" value={fmtINR(slip.reimbursements_total)} />
                            </div>
                        ) : null}
                    </SectionCard>

                    {/* Breakups */}
                    {hasBreakupContent && (
                        <SectionCard icon={Wallet} title="Breakup">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <BreakupList title="Earnings" data={slip.earnings_breakup} valueClass="text-emerald-600 dark:text-emerald-400" />
                                <BreakupList title="Deductions" data={slip.deductions_breakup} valueClass="text-rose-600 dark:text-rose-400" />
                                <BreakupList title="Credits" data={slip.credits_breakup} valueClass="text-emerald-600 dark:text-emerald-400" />
                                <BreakupList title="Debits" data={slip.debit_breakup} valueClass="text-rose-600 dark:text-rose-400" />
                                <BreakupList title="Reimbursements" data={slip.reimbursements} valueClass="text-indigo-600 dark:text-indigo-400" />
                            </div>
                        </SectionCard>
                    )}

                    {/* YTD */}
                    {slip.ytd && (
                        <SectionCard icon={Wallet} title="Year to Date">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                <Figure label="YTD Earnings" value={fmtINR(slip.ytd.earnings)} tone="teal" />
                                <Figure label="YTD Deductions" value={fmtINR(slip.ytd.deductions)} />
                                <Figure label="YTD Net Pay" value={fmtINR(slip.ytd.net_pay)} tone="green" />
                            </div>
                            {slip.ytd.financial_year && (
                                <p className="text-[11px] text-gray-400 mt-2">
                                    FY {slip.ytd.financial_year}{slip.ytd.upto_month ? ` · up to ${slip.ytd.upto_month}` : ""}
                                </p>
                            )}
                        </SectionCard>
                    )}
                </>
            )}
        </div>
    );
};

/* =====================================================
   ROOT
===================================================== */
const MyProfile = () => {
    const { fetch, create } = useApiMethods();
    const fileRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [tab, setTab] = useState("overview");

    const companyName = sessionStorage.getItem("companyName");
    const loggedInEmployeeId = sessionStorage.getItem("employee_id");

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(EMPLOYEE_PROFILE);
            if (res?.status === "success") {
                setData(res.data);
                // Keep the header avatar (reads sessionStorage.profile) in sync.
                if (res.data?.image_url) sessionStorage.setItem("profile", res.data.image_url);
            } else if (!silent) {
                ToastMsg("error", res?.message || "Failed to load profile.");
            }
        } catch (err) {
            if (!silent) ToastMsg("error", err?.message || "Failed to load profile.");
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Refresh lock state when HR toggles Lock Process or user opens Salary/Payslips.
    useEffect(() => {
        const refreshLockState = () => {
            if (tab === "salary" || tab === "payslips") {
                load(true);
            }
        };
        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, refreshLockState);
        return () => window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, refreshLockState);
    }, [tab, load]);

    const handleTabChange = (key) => {
        setTab(key);
        if (key === "salary" || key === "payslips") {
            load(true);
        }
    };

    // ---- Photo change (POST multipart, field "profile") ----
    const onPickPhoto = () => fileRef.current?.click();

    const onPhotoSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            ToastMsg("error", "Please choose an image file.");
            return;
        }
        const formData = new FormData();
        formData.append("profile", file);
        setUploading(true);
        try {
            const res = await create(EMPLOYEE_PROFILE, formData, true);
            if (res?.status === "success") {
                ToastMsg("success", "Profile photo updated.");
                if (res.data?.image_url) sessionStorage.setItem("profile", res.data.image_url);
                await load();
            } else {
                ToastMsg("error", res?.message || "Failed to update photo.");
            }
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to update photo.");
        } finally {
            setUploading(false);
        }
    };

    const personal = data?.Personal_Information || {};
    const bank = data?.bank_details || {};
    const assets = data?.Asset_Information || [];
    const documents = data?.Documentation || [];

    // Backend returns salary oldest-first; show latest first (newest created_date).
    const salary = useMemo(() => {
        const list = data?.Salary_Info || [];
        return [...list].sort(
            (a, b) =>
                dayjs(b.created_date).valueOf() - dayjs(a.created_date).valueOf() ||
                (b.id || 0) - (a.id || 0)
        );
    }, [data]);

    const salaryPayslipLocked = !!data?.salary_payslip_locked;
    const salaryPayslipLockMessage = data?.salary_payslip_lock_message;
    const salaryPayslipLockTitle = (salaryPayslipLockMessage || "").toLowerCase().includes("not enabled")
        ? "Payslip access disabled"
        : "Access temporarily locked";

    // Always show read-only tabs; empty sections render an in-tab empty state.
    const tabs = useMemo(() => [
        { key: "overview", label: "Overview", icon: UserRound },
        { key: "personal", label: "Personal", icon: IdCard },
        { key: "bank", label: "Bank", icon: Landmark },
        { key: "salary", label: "Salary", icon: Wallet },
        { key: "payslips", label: "Payslips", icon: ReceiptText },
        { key: "deposits", label: "Deposits", icon: PiggyBank },
        { key: "penalties", label: "Penalties", icon: Scale },
        { key: "documents", label: "Documents", icon: FileText },
        { key: "assets", label: "Assets", icon: Package },
        { key: "activity", label: "Activity", icon: Activity },
        { key: "notifications", label: "Notifications", icon: Bell },
    ], []);

    const avatarSrc = data?.image_url || sessionStorage.getItem("profile") || male;

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={UserRound}
                title="My Profile"
                subtitle="Your personal, work, bank and document details"
            />

            {loading ? (
                <div className="flex items-center justify-center py-24">
                    <Loader2 className="w-7 h-7 animate-spin text-sidebar-primary" />
                </div>
            ) : !data ? (
                <div className="px-3 pb-4">
                    <EmptyState text="Could not load your profile." />
                </div>
            ) : (
                <div className="px-3 pb-6 space-y-3">
                    {/* ---------- Hero ---------- */}
                    <div className="relative overflow-hidden rounded-2xl border border-sidebar-primary/20 shadow-sm bg-gradient-to-br from-sidebar-primary to-teal-600 text-white p-5">
                        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
                        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                            {/* Avatar + change */}
                            <div className="relative shrink-0">
                                <img
                                    src={avatarSrc}
                                    alt={data.name}
                                    onError={(ev) => { ev.currentTarget.src = male; }}
                                    className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/30 bg-white"
                                />
                                <button
                                    type="button"
                                    onClick={onPickPhoto}
                                    disabled={uploading}
                                    title="Change photo"
                                    className="absolute -bottom-2 -right-2 h-8 w-8 inline-flex items-center justify-center rounded-full bg-white text-sidebar-primary shadow-md hover:scale-105 transition-transform disabled:opacity-60"
                                >
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                                </button>
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoSelected} />
                            </div>

                            {/* Identity */}
                            <div className="min-w-0 flex-1">
                                <h2 className="text-2xl font-bold leading-tight truncate" title={show(data.name)}>{show(data.name)}</h2>
                                <p className="text-white/85 text-sm mt-0.5 truncate" title={[data.designation, data.department].filter(Boolean).join(" · ")}>
                                    {show(data.designation)}
                                    {data.department ? ` · ${data.department}` : ""}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-white/80">
                                    <span className="inline-flex items-center gap-1" title={`Employee code: ${show(data.employee_id)}`}><IdCard className="w-3.5 h-3.5" /> {show(data.employee_id)}</span>
                                    {companyName && <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {companyName}</span>}
                                    {data.employee_type && <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {data.employee_type}</span>}
                                </div>
                            </div>

                            {/* Quick contact */}
                            <div className="sm:text-right space-y-1 text-sm flex gap-1.5">
                                {data.email && <p className="inline-flex sm:justify-end items-center gap-1.5 text-white/90"><Mail className="w-3.5 h-3.5" /> {data.email} |</p>}
                                {data.phone && <p className="inline-flex sm:justify-end items-center gap-1.5 text-white/90"><Phone className="w-3.5 h-3.5" /> {data.phone} |</p>}
                                {data.work_location && <p className="inline-flex sm:justify-end items-center gap-1.5 text-white/90"><MapPin className="w-3.5 h-3.5" /> {data.work_location}</p>}
                            </div>
                        </div>
                    </div>

                    {/* ---------- Tabs ---------- */}
                    <div className="flex items-center gap-1 overflow-x-auto bg-gray-100 dark:bg-gray-900/70 rounded-xl p-1">
                        {tabs.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => handleTabChange(t.key)}
                                className={cn(
                                    "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all",
                                    tab === t.key
                                        ? "bg-background text-sidebar-primary shadow-sm"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                )}
                            >
                                <t.icon className="w-4 h-4" /> {t.label}
                            </button>
                        ))}
                    </div>

                    {/* ---------- Overview ---------- */}
                    {tab === "overview" && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <SectionCard icon={Briefcase} title="Work">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field icon={Briefcase} label="Designation" value={data.designation} />
                                    <Field icon={Building2} label="Department" value={data.department} />
                                    <Field icon={UserCog} label="Reporting Manager" value={data.reporting_manager} />
                                    <Field icon={Clock} label="Shift" value={data.shift} />
                                    <Field icon={MapPin} label="Work Location" value={data.work_location} />
                                    <Field icon={IdCard} label="Employee ID" value={data.employee_id} />
                                </div>
                            </SectionCard>

                            <SectionCard icon={Mail} title="Contact">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field icon={Mail} label="Email" value={data.email} />
                                    <Field icon={Phone} label="Phone" value={data.phone} />
                                    <Field icon={MapPin} label="Location" value={data.location} />
                                    <Field icon={Droplet} label="Blood Group" value={data.blood_group} />
                                </div>
                            </SectionCard>
                        </div>
                    )}

                    {/* ---------- Personal ---------- */}
                    {tab === "personal" && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <SectionCard icon={UserRound} title="Personal">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field icon={Users} label="Gender" value={personal.gender} />
                                    <Field icon={Cake} label="Date of Birth" value={personal.date_of_birth ? fmtDate(personal.date_of_birth) : null} />
                                    <Field icon={Droplet} label="Blood Group" value={personal.blood_group} />
                                    <Field icon={Phone} label="Personal Mobile" value={personal.personal_mobile_no} />
                                    <Field icon={Mail} label="Personal Email" value={personal.personal_email_id} />
                                </div>
                            </SectionCard>

                            <SectionCard icon={ShieldAlert} title="Emergency Contact">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field icon={UserRound} label="Contact Person" value={personal.emergency_contact_person || personal.contact_person_name} />
                                    <Field icon={Phone} label="Contact Number" value={personal.emergency_contact_number || personal.contact_person_number} />
                                    <Field icon={Users} label="Relationship" value={personal.emergency_contact_relationship || personal.relationship_type} />
                                </div>
                            </SectionCard>

                            <SectionCard icon={MapPin} title="Permanent Address">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Address" value={personal.permanant_address} className="sm:col-span-2" />
                                    <Field label="City" value={personal.city} />
                                    <Field label="State" value={personal.state} />
                                    <Field label="Country" value={personal.country} />
                                    <Field label="Pincode" value={personal.pincode} />
                                </div>
                            </SectionCard>

                            <SectionCard icon={MapPin} title="Present Address">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Address" value={personal.present_address} className="sm:col-span-2" />
                                    <Field label="City" value={personal.persent_city} />
                                    <Field label="State" value={personal.present_state} />
                                    <Field label="Country" value={personal.present_country} />
                                    <Field label="Pincode" value={personal.present_pincode} />
                                </div>
                            </SectionCard>
                        </div>
                    )}

                    {/* ---------- Bank ---------- */}
                    {tab === "bank" && (
                        <SectionCard icon={Landmark} title="Bank Details">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <Field icon={Landmark} label="Bank Name" value={bank.bank_name} />
                                <Field icon={IdCard} label="Account Number" value={bank.account_number} />
                                <Field icon={IdCard} label="IFSC" value={bank.ifsc} />
                            </div>
                        </SectionCard>
                    )}

                    {/* ---------- Payslips ---------- */}
                    {tab === "payslips" && (
                        <PayrollProfileLockOverlay
                            locked={salaryPayslipLocked}
                            message={salaryPayslipLockMessage}
                            title={salaryPayslipLockTitle}
                        >
                            <PayslipsTab locked={salaryPayslipLocked} />
                        </PayrollProfileLockOverlay>
                    )}

                    {/* ---------- Documents ---------- */}
                    {tab === "documents" && (
                        <SectionCard icon={FileText} title="Documents">
                            {documents.length === 0 ? (
                                <EmptyState text="No documents uploaded yet. Uploaded ID proofs and certificates will appear here." />
                            ) : (
                                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                    {documents.map((doc, i) => (
                                        <div key={i} className="flex items-center gap-3 py-2.5">
                                            <div className="h-9 w-9 shrink-0 rounded-lg bg-sidebar-primary/10 text-sidebar-primary flex items-center justify-center">
                                                <FileText className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{show(doc.document)}</p>
                                                <p className="text-xs text-gray-400 truncate">
                                                    {doc.document_file_name || "—"}
                                                    {doc.uploaded_on ? ` · ${fmtDate(doc.uploaded_on)}` : ""}
                                                </p>
                                            </div>
                                            {doc.document_url ? (
                                                <a
                                                    href={doc.document_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sidebar-primary hover:bg-sidebar-primary/5 transition-colors shrink-0"
                                                >
                                                    <Download className="w-3.5 h-3.5" /> View
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-300 dark:text-gray-600 shrink-0">No file</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </SectionCard>
                    )}

                    {/* ---------- Assets ---------- */}
                    {tab === "assets" && (
                        <SectionCard icon={Package} title="Issued Assets">
                            {assets.length === 0 ? (
                                <EmptyState text="No assets issued." />
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                                <th className="py-2 pr-3 font-medium">Asset ID</th>
                                                <th className="py-2 pr-3 font-medium">Asset</th>
                                                <th className="py-2 pr-3 font-medium">Issued Date</th>
                                                <th className="py-2 font-medium">Issued By</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {assets.map((a, i) => (
                                                <tr key={i} className="text-gray-700 dark:text-gray-200">
                                                    <td className="py-2.5 pr-3">{show(a.asset_id)}</td>
                                                    <td className="py-2.5 pr-3 font-medium">{show(a.asset_name)}</td>
                                                    <td className="py-2.5 pr-3">{fmtDate(a.issued_date)}</td>
                                                    <td className="py-2.5">{show(a.issued_by)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </SectionCard>
                    )}

                    {/* ---------- Salary (latest first) ---------- */}
                    {tab === "salary" && (
                        <PayrollProfileLockOverlay
                            locked={salaryPayslipLocked}
                            message={salaryPayslipLockMessage}
                            title={salaryPayslipLockTitle}
                        >
                            {salary.length === 0 ? (
                                <SectionCard icon={Wallet} title="Salary Information">
                                    <EmptyState text="No salary structure has been assigned yet. Contact HR if you believe this is incorrect." />
                                </SectionCard>
                            ) : (
                                <div className="space-y-3">
                                    {/* Current package */}
                                    <SalaryCard salary={salary[0]} isCurrent />

                                    {/* Divider + helper between current and history */}
                                    {salary.length > 1 && (
                                        <div className="pt-2">
                                            <div className="flex items-center gap-3">
                                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                                    Previous Revisions
                                                </span>
                                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                            </div>
                                            <p className="text-center text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                                                Earlier salary structures, most recent first. Only the current package applies to your payroll.
                                            </p>
                                        </div>
                                    )}

                                    {salary.slice(1).map((s, i) => (
                                        <SalaryCard key={s.id ?? i} salary={s} />
                                    ))}
                                </div>
                            )}
                        </PayrollProfileLockOverlay>
                    )}

                    {tab === "activity" && loggedInEmployeeId && (
                        <EmployeeActivityContent employeeID={loggedInEmployeeId} selfView />
                    )}

                    {tab === "notifications" && <MyProfileNotificationsTab />}

                    {tab === "deposits" && <MyProfileDepositsTab />}
                    {tab === "penalties" && <MyProfilePenaltiesTab />}
                </div>
            )}
        </ContainerCard>
    );
};

export default MyProfile;
